// 用户侧对战预约（后端 internal/matchserver/user_handler.go）。
import { get, post } from "./client";
import type { MatchEvent, ServerMatch, ServerPool } from "./match-servers";
import type { DemoStatus, Page } from "./types";

export interface RosterMember {
  user_id: string;
  name: string;
  role: string;
  steamid64?: string;
}

export interface BookingRoster {
  team_id: string;
  team_name: string;
  players: RosterMember[];
  coaches: RosterMember[];
  unlinked: RosterMember[];
}

export interface ConnectInfo {
  address: string;
  password?: string;
  command: string;
  gotv_address: string | null;
  server_name: string;
  region: string;
}

export interface UserMatch extends ServerMatch {
  viewer_team_id: string;
  viewer_side: "team1" | "team2";
  can_manage: boolean;
  can_accept: boolean;
  can_cancel: boolean;
  booked_by_name: string | null;
  pool_name: string | null;
  pool_region: string | null;
  connect: ConnectInfo | null;
  /** 选边协商中等待哪一方确认；开刀局、手填对手或已定稿时为 null。 */
  sides_turn: "team1" | "team2" | null;
  /** 打满全部回合（不提前结束、不打加时）。 */
  playout: boolean;
}

/** 是否轮到我方处理：被约方接受挑战，或对方修改了选边待我方确认（不论是否有权限操作）。 */
export const myTurn = (m: UserMatch) => m.status === "pending" && m.viewer_side === (m.sides_turn ?? "team2");
/** 对方改过选边、交回我方确认。 */
export const isCountered = (m: UserMatch) => myTurn(m) && (m.viewer_side === "team1" || Object.keys(m.team2.players ?? {}).length > 0);

export interface OpponentTeam {
  id: string;
  name: string;
  slug: string;
  avatar_url: string | null;
}

export interface BookingInput {
  team_id: string;
  pool_id: string;
  start: string;
  num_maps: number;
  mode: "5v5" | "2v2";
  /** 按顺序的比赛地图（= num_maps 张），服务器一张打完自动切到下一张。 */
  maplist: string[];
  /** 每张图刀局选边；false 时随机分配阵营。 */
  knife: boolean;
  /** 打满全部回合（不提前结束、不打加时）。 */
  playout: boolean;
  /** 不开刀局时每张图的起始阵营（发起方为 team1）。 */
  map_sides?: string[];
  /** 手填对手时声明已与对手确认选边。 */
  sides_confirmed?: boolean;
  demo_consent: boolean;
  title?: string;
  extra_players: Record<string, string>;
  opponent_type: "team" | "manual";
  opponent_team_id?: string;
  opponent_name?: string;
  opponent_players?: Record<string, string>;
}

export interface MatchDemo {
  demo_id: string;
  map_name: string;
  map_number: number | null;
  demo_status: DemoStatus | null;
  visibility: "full" | "restricted" | null;
}

export const qkb = {
  all: ["bookings"] as const,
  pools: (start: string, numMaps: number) => ["bookings", "pools", start, numMaps] as const,
  roster: (teamId: string) => ["bookings", "roster", teamId] as const,
  opponents: (teamId: string, q: string) => ["bookings", "opponents", teamId, q] as const,
  upcoming: (teamId: string) => ["bookings", "list", teamId, "upcoming"] as const,
  history: (teamId: string) => ["bookings", "list", teamId, "history"] as const,
  match: (id: string) => ["bookings", "match", id] as const,
  events: (id: string) => ["bookings", "match", id, "events"] as const,
};

export const epb = {
  pools: (start: string, numMaps: number) => get<{ items: ServerPool[] }>("/match-pools", { start, num_maps: numMaps }),
  roster: (teamId: string) => get<BookingRoster>("/match-roster", { team_id: teamId }),
  opponents: (teamId: string, q: string) => get<{ items: OpponentTeam[] }>("/match-opponents", { team_id: teamId, q }),
  upcoming: (teamId: string) => get<Page<UserMatch>>("/matches", { team_id: teamId, scope: "upcoming" }),
  history: (teamId: string, cursor?: string) => get<Page<UserMatch>>("/matches", { team_id: teamId, scope: "history", cursor, limit: 20 }),
  book: (body: BookingInput) => post<UserMatch>("/matches", body),
  match: (id: string) => get<{ match: UserMatch; demos: MatchDemo[] }>(`/matches/${id}`),
  events: (id: string) => get<{ items: MatchEvent[] }>(`/matches/${id}/events`),
  /** 回应挑战 / 选边方案：map_sides 省略或不变表示同意，否则交回对方确认。 */
  accept: (id: string, extra: Record<string, string>, mapSides?: string[]) => post<UserMatch>(`/matches/${id}/accept`, { extra_players: extra, map_sides: mapSides }),
  decline: (id: string) => post<UserMatch>(`/matches/${id}/decline`),
  cancel: (id: string, reason?: string) => post<UserMatch>(`/matches/${id}/cancel`, reason ? { reason } : {}),
};
